import React from 'react';
import { useIntl } from 'react-intl';
import { Alert } from 'reactstrap';
import { TypedMemo } from '../../../common/types';
import { platformMeta } from '../platformMeta';
import { SerializedScheduledPost } from '../types';
import { postFailMessages, postFailUnknownMessage } from './postFailMessages';
import { withBrandWord } from '../../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Promote pillar; each part uses its own slice of it */
import styles from '../Promote.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PostFailureNoticeProps {
  post: SerializedScheduledPost;
}

/**
 * Design product-gaps a1 and a2: why a failed post did not go out. A reason the app knows gets
 * its own sentence; the reason as stored shows under it, so support and the artist read the same
 * words. A failed post keeps the scheduled-post unit it took, and the notice says so.
 */
const PostFailureNoticeComponent: React.FC<PostFailureNoticeProps> = ({ post }) => {
  const intl = useIntl();
  const platform = platformMeta[post.platform].name;
  const sentence =
    post.failCode !== null
      ? intl.formatMessage(postFailMessages[post.failCode], { platform })
      : intl.formatMessage(postFailUnknownMessage, { platform });
  const hasRaw = post.failReason !== null && post.failReason !== '';

  return (
    <>
      <Alert color="danger" className="mt-3 mb-0">
        <strong className="d-block">
          {intl.formatMessage({ id: 'app.sml.promote.post.fail.title', defaultMessage: 'Why it did not go out' })}
        </strong>
        {post.failCode === null && !hasRaw
          ? intl.formatMessage({
              id: 'app.sml.promote.post.fail.noReason',
              defaultMessage: 'We have no reason for this failure. Schedule the post again, or write to support.',
            })
          : withBrandWord(sentence)}
        {hasRaw ? <div className={styles.failRaw}>{post.failReason}</div> : null}
      </Alert>
      <div className={styles.statNote}>
        {intl.formatMessage({
          id: 'app.sml.promote.post.fail.quota',
          defaultMessage: "A failed post still counts toward this month's scheduled posts.",
        })}
      </div>
    </>
  );
};

export const PostFailureNotice = TypedMemo(PostFailureNoticeComponent);
